import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import WaveBackground from '../login-screen/components/WaveBackground';

// Forced password change on first sign-in.
//
// Client logins (the read-only access for a company's key person) are provisioned by us with a
// TEMPORARY password, and the account is flagged `must_change_password`. ProtectedRoute renders
// this screen INSTEAD of the dashboard while that flag is set, so it can't be skipped by
// navigating elsewhere — every route goes through the same wrapper. Saving clears the flag in
// the same call that sets the password (see AuthContext.updatePassword).
//
// If the person loses the password, we re-run the provisioning script with --reset-password:
// it sets a new temporary one and raises the flag again, bringing them back here.
const MIN_LEN = 8;

const ChangePasswordScreen = () => {
  const { updatePassword, signOut, user } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < MIN_LEN;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= MIN_LEN && password === confirm && !loading;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const { error: updErr } = await updatePassword(password);
      if (updErr) {
        setError(updErr?.message || 'Could not update the password. Please try again.');
        return;
      }
      // No navigation needed: Supabase emits USER_UPDATED, AuthContext refreshes `user`
      // without the flag, and ProtectedRoute renders the dashboard on the next paint.
    } finally {
      setLoading(false);
    }
  };

  const field = (id, label, value, onChange, autoComplete) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon name="Lock" size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
        </div>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e?.target?.value)}
          required
          autoComplete={autoComplete}
          disabled={loading}
          className="w-full h-11 pl-10 pr-11 rounded-lg text-sm transition-all duration-200"
          style={{
            backgroundColor: 'var(--color-input)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-foreground)',
            outline: 'none',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-accent)';
            e.target.style.boxShadow = '0 0 0 3px rgba(6, 214, 245, 0.12)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--color-border)';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded transition-colors"
          style={{ color: 'var(--color-muted-foreground)' }}
          tabIndex={-1}
        >
          <Icon name={show ? 'EyeOff' : 'Eye'} size={16} strokeWidth={2} />
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="dark min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: '#080d1e', position: 'relative' }}
    >
      <WaveBackground />
      <div
        className="w-full max-w-[420px] rounded-2xl p-8"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.82)',
          border: '1px solid rgba(34, 211, 238, 0.22)',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(52, 102, 244, 0.10)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="flex flex-col items-center gap-4 mb-7">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <Icon name="KeyRound" size={30} color="#FFFFFF" strokeWidth={2} />
          </div>
          <div className="text-center">
            <h1
              className="text-xl font-bold tracking-tight"
              style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}
            >
              Set your password
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
              Your account was created with a temporary password. Choose a new one to continue.
            </p>
            {user?.email && (
              <p className="text-xs mt-2" style={{ color: 'var(--color-muted-foreground)' }}>
                {user.email}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-lg"
              style={{
                backgroundColor: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid rgba(220, 38, 38, 0.25)',
              }}
            >
              <Icon name="AlertCircle" size={16} color="var(--color-error)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <span className="text-sm" style={{ color: 'var(--color-error)' }}>{error}</span>
            </div>
          )}

          {field('new-password', 'New password', password, setPassword, 'new-password')}
          {field('confirm-password', 'Confirm password', confirm, setConfirm, 'new-password')}

          <p className="text-xs -mt-2" style={{ color: tooShort ? 'var(--color-error)' : 'var(--color-muted-foreground)' }}>
            {tooShort ? `At least ${MIN_LEN} characters.` : `Use at least ${MIN_LEN} characters.`}
          </p>
          {mismatch && (
            <p className="text-xs -mt-3" style={{ color: 'var(--color-error)' }}>
              The two passwords don't match.
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all duration-200"
            style={{
              backgroundColor: !canSubmit ? 'var(--color-muted)' : 'var(--color-primary)',
              color: !canSubmit ? 'var(--color-muted-foreground)' : '#FFFFFF',
              cursor: !canSubmit ? 'not-allowed' : 'pointer',
              boxShadow: !canSubmit ? 'none' : 'var(--shadow-md)',
            }}
          >
            {loading ? (
              <>
                <div
                  className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                  style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }}
                />
                Saving…
              </>
            ) : (
              <>
                <Icon name="Check" size={16} strokeWidth={2} />
                Save and continue
              </>
            )}
          </button>
        </form>

        <button
          type="button"
          onClick={() => signOut()}
          className="w-full mt-4 text-xs transition-colors"
          style={{ color: 'var(--color-muted-foreground)' }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordScreen;
